import { Injectable } from "@angular/core";
import { StoreService } from "..";
import { catchError, from, map, Observable, tap } from "rxjs";
import { MessageTemplateApiService } from "../../services/api/message-template-api.service";
import { MessageTemplate, MessageTemplateBase } from "../../models/message-template.model";

@Injectable({
  providedIn: 'root'
})
export class MessageTemplateService {
  constructor(
    private store: StoreService,
    private messageTemplateApiService: MessageTemplateApiService
  ) { }

  get messageTemplates$(): Observable<MessageTemplate[]> {
    return this.store.state$.pipe(map(state => Object.values(state.messageTemplates)));
  }

  load(): Observable<MessageTemplate[]> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return this.messageTemplateApiService.getAll().pipe(
      tap((templates) => {
        const state = { ...this.store.state };
        templates.forEach((t) => state.messageTemplates[t.id] = t);
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading message templates:', error);
        throw error;
      })
    );
  }

  addMessageTemplate(template: MessageTemplateBase): Observable<MessageTemplate> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.messageTemplateApiService.add(template)).pipe(
      tap((newTemplate) => {
        this.updateState(newTemplate);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error adding message template:', error);
        throw error;
      })
    );
  }

  updateMessageTemplate(template: MessageTemplate): Observable<MessageTemplate> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.messageTemplateApiService.update(template)).pipe(
      tap((updatedTemplate) => {
        this.updateState(updatedTemplate);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error updating message template:', error);
        throw error;
      })
    );
  }

  private updateState(template: MessageTemplate) {
    const state = { ...this.store.state };
    state.messageTemplates[template.id] = template;
    state.loading = false;
    this.store.setState(state);
  }

  deleteMessageTemplate(id: number): Observable<void> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.messageTemplateApiService.delete(id)).pipe(
      tap(() => {
        const state = { ...this.store.state };
        delete state.messageTemplates[id];
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error deleting message template:', error);
        throw error;
      })
    );
  }

  clear(): void {
    const state = { ...this.store.state };
    state.messageTemplates = {};
    this.store.setState(state);
  }
}
